// React Query hooks for the Players data (FE5): search, bio, the player-scoped
// competition/season lists, notes (read + create), and headline stats. Queries
// are disabled until their inputs are known.

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import type {
  PlayerSearchResponse,
  PlayerBioResponse,
  PlayerCompetitionsResponse,
  PlayerSeasonsResponse,
  PlayerNotesResponse,
  CreateNoteResponse,
  PlayerShotsResponse,
  PlayerPassesResponse,
  MatchGoalsResponse,
  PlayerAppearancesResponse,
  HopsDistribution,
} from "@catbento/shared";
import { apiGet, apiSend } from "../api.js";
import { toTiptapDoc } from "../lib/noteText.js";

const FIVE_MIN = 5 * 60 * 1000;

/**
 * A competition argument that can be a single id or a SET (C2d — the Matches
 * widget scopes across ≥1 competition). The endpoints accept a comma-separated
 * `competitionId`, so a single number and a list share one code path. Returns the
 * query-string value, or null when there's nothing to query (so the hook stays
 * disabled) — an empty array means "no competitions selected".
 */
export type CompetitionArg = number | number[] | null;
export function compParam(competition: CompetitionArg): string | null {
  if (competition === null) return null;
  const ids = Array.isArray(competition) ? competition : [competition];
  return ids.length > 0 ? ids.join(",") : null;
}

/** Type-ahead player search. Disabled until the query is long enough (the
 *  backend also returns empty for < 3 chars — this just avoids the round trip). */
export function usePlayerSearch(query: string): UseQueryResult<PlayerSearchResponse, Error> {
  const q = query.trim();
  return useQuery({
    queryKey: ["player-search", q],
    enabled: q.length >= 3,
    queryFn: () => apiGet<PlayerSearchResponse>(`/player-search?q=${encodeURIComponent(q)}`),
    staleTime: FIVE_MIN,
  });
}

export function usePlayerBio(playerId: number | null): UseQueryResult<PlayerBioResponse, Error> {
  return useQuery({
    queryKey: ["player", playerId],
    enabled: playerId !== null,
    queryFn: () => apiGet<PlayerBioResponse>(`/player?playerId=${playerId}`),
  });
}

export function usePlayerCompetitions(
  playerId: number | null,
  // Optional (C2d): given → competitions the player played in THAT season (the
  // Matches/stats competition options); omitted → every competition in the career.
  seasonId: number | null = null
): UseQueryResult<PlayerCompetitionsResponse, Error> {
  return useQuery({
    queryKey: ["player-competitions", playerId, seasonId],
    enabled: playerId !== null,
    queryFn: () =>
      apiGet<PlayerCompetitionsResponse>(
        `/player-competitions?playerId=${playerId}${seasonId !== null ? `&seasonId=${seasonId}` : ""}`
      ),
    staleTime: FIVE_MIN,
  });
}

/** Every season a player played, across their whole career (season-first — no
 *  competition filter). Shares the cache key the v2 stats cards use. */
export function usePlayerCareerSeasons(
  playerId: number | null
): UseQueryResult<PlayerSeasonsResponse, Error> {
  return useQuery({
    queryKey: ["player-seasons-career", playerId],
    enabled: playerId !== null,
    queryFn: () => apiGet<PlayerSeasonsResponse>(`/player-seasons?playerId=${playerId}`),
    staleTime: FIVE_MIN,
  });
}

export function usePlayerSeasons(
  playerId: number | null,
  competitionId: number | null
): UseQueryResult<PlayerSeasonsResponse, Error> {
  return useQuery({
    queryKey: ["player-seasons", playerId, competitionId],
    enabled: playerId !== null && competitionId !== null,
    queryFn: () =>
      apiGet<PlayerSeasonsResponse>(`/player-seasons?playerId=${playerId}&competitionId=${competitionId}`),
    staleTime: FIVE_MIN,
  });
}

export function usePlayerNotes(playerId: number | null): UseQueryResult<PlayerNotesResponse, Error> {
  return useQuery({
    queryKey: ["player-notes", playerId],
    enabled: playerId !== null,
    queryFn: () => apiGet<PlayerNotesResponse>(`/player-notes?playerId=${playerId}`),
  });
}

/** Create a note on a player (plain text wrapped in a minimal Tiptap doc), then
 *  refresh that player's notes list. */
export function useCreateNote(playerId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) =>
      apiSend<CreateNoteResponse>("POST", "/player-notes", {
        playerId,
        note: toTiptapDoc(text),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["player-notes", playerId] });
    },
  });
}

/** A player's shot events for one competition+season, for the shot map (FE6).
 *  Disabled until player+comp+season are all known. `retry: 1` because
 *  `/player-shots` is a cold-prone VPC Lambda that can transient-500 on the first
 *  hit (a heavy raw-event read) — one retry rides over that without masking real
 *  errors. Cached a while: the same player-season shots don't change mid-session. */
export function usePlayerShots(
  playerId: number | null,
  competition: CompetitionArg,
  seasonId: number | null
): UseQueryResult<PlayerShotsResponse, Error> {
  const cid = compParam(competition);
  return useQuery({
    queryKey: ["player-shots", playerId, cid, seasonId],
    enabled: playerId !== null && cid !== null && seasonId !== null,
    queryFn: () =>
      apiGet<PlayerShotsResponse>(
        `/player-shots?playerId=${playerId}&competitionId=${cid}&seasonId=${seasonId}`
      ),
    retry: 1,
    staleTime: FIVE_MIN,
  });
}

/** A player's pass events for one competition+season, for the pass map (FE6b).
 *  Same cold-Lambda + gating story as `usePlayerShots`. Note the payload can be
 *  large (a busy passer's full season is ~1000+ passes / a few MB) — cached
 *  `FIVE_MIN` so re-viewing the same player-season is instant. */
export function usePlayerPasses(
  playerId: number | null,
  competition: CompetitionArg,
  seasonId: number | null
): UseQueryResult<PlayerPassesResponse, Error> {
  const cid = compParam(competition);
  return useQuery({
    queryKey: ["player-passes", playerId, cid, seasonId],
    enabled: playerId !== null && cid !== null && seasonId !== null,
    queryFn: () =>
      apiGet<PlayerPassesResponse>(
        `/player-passes?playerId=${playerId}&competitionId=${cid}&seasonId=${seasonId}`
      ),
    retry: 1,
    staleTime: FIVE_MIN,
  });
}

/** Every goal (for and against) in the matches a player appeared in for one
 *  competition+season — the Matches "Team Goals" overlay. Small payload (a few
 *  goals per match); same cold-Lambda + gating story as the event maps. */
export function useMatchGoals(
  playerId: number | null,
  competition: CompetitionArg,
  seasonId: number | null
): UseQueryResult<MatchGoalsResponse, Error> {
  const cid = compParam(competition);
  return useQuery({
    queryKey: ["match-goals", playerId, cid, seasonId],
    enabled: playerId !== null && cid !== null && seasonId !== null,
    queryFn: () =>
      apiGet<MatchGoalsResponse>(
        `/match-goals?playerId=${playerId}&competitionId=${cid}&seasonId=${seasonId}`
      ),
    retry: 1,
    staleTime: FIVE_MIN,
  });
}

/** A player's per-match appearances for one competition+season, for the
 *  appearances timeline (#5). Same cold-Lambda + gating story as the event maps;
 *  cached FIVE_MIN (a player-season's appearances don't change mid-session). */
export function usePlayerAppearances(
  playerId: number | null,
  competition: CompetitionArg,
  seasonId: number | null
): UseQueryResult<PlayerAppearancesResponse, Error> {
  const cid = compParam(competition);
  return useQuery({
    queryKey: ["player-appearances", playerId, cid, seasonId],
    enabled: playerId !== null && cid !== null && seasonId !== null,
    queryFn: () =>
      apiGet<PlayerAppearancesResponse>(
        `/player-appearances?playerId=${playerId}&competitionId=${cid}&seasonId=${seasonId}`
      ),
    retry: 1,
    staleTime: FIVE_MIN,
  });
}

/** A player's minutes per position for one competition+season — POST /player-stats
 *  grouped by position_id. Used to auto-select the radar template (top position).
 *  Rows: { position_id: "<code>", minutes }. */
export interface PositionMinutesRow {
  position_id: string;
  minutes: number;
}
export interface PositionMinutesResponse {
  rows: PositionMinutesRow[];
}
export function usePlayerPositionMinutes(
  playerId: number | null,
  competitionId: number | null,
  seasonId: number | null
): UseQueryResult<PositionMinutesResponse, Error> {
  return useQuery({
    queryKey: ["position-minutes", playerId, competitionId, seasonId],
    enabled: playerId !== null && competitionId !== null && seasonId !== null,
    retry: 1,
    staleTime: FIVE_MIN,
    queryFn: () =>
      apiSend<PositionMinutesResponse>("POST", "/player-stats", {
        keys: ["minutes"],
        where: { player_id: [playerId], competition_id: [competitionId], season_id: [seasonId] },
        groupBy: ["position_id"],
      }),
  });
}

/** Headline stats for a player in one competition+season. POST /player-stats is
 *  read-only; grouped by player so it collapses to a single row. */
/**
 * The HOPS rating distribution for one population (men's / women's). The curve is
 * static reference data, so cache it hard (staleTime Infinity) — one fetch per
 * gender for the whole session. Disabled until the caller resolves the gender.
 */
export function useHopsDistribution(
  isWomen: boolean | null
): UseQueryResult<HopsDistribution, Error> {
  return useQuery({
    queryKey: ["hops-distribution", isWomen],
    enabled: isWomen !== null,
    staleTime: Infinity,
    queryFn: () => apiGet<HopsDistribution>(`/hops-distribution?isWomen=${isWomen}`),
  });
}

export interface StatsResponse {
  rows: Record<string, number | string | null>[];
}
export function usePlayerStats(
  playerId: number | null,
  competitionId: number | null,
  seasonId: number | null,
  keys: string[],
  // Optional position CODES ("CF", "RCF"…). When given, the per-90 stats are
  // computed over ONLY the minutes played in those positions — the Radar passes
  // its template's positions so its numbers match old Catalyst (which filters the
  // radar to the template's positions). Omitted → the player's overall per-90.
  positions?: string[]
): UseQueryResult<StatsResponse, Error> {
  return useQuery({
    queryKey: ["player-stats", playerId, competitionId, seasonId, keys, positions ?? null],
    // keys can be empty while a caller's metric catalogue is still loading (Full
    // Stats); the backend 400s on empty keys, so wait until we have some.
    enabled: playerId !== null && competitionId !== null && seasonId !== null && keys.length > 0,
    queryFn: () =>
      apiSend<StatsResponse>("POST", "/player-stats", {
        keys,
        where: {
          player_id: [playerId],
          competition_id: [competitionId],
          season_id: [seasonId],
          ...(positions && positions.length > 0 ? { position_id: positions } : {}),
        },
        groupBy: ["player_id"],
      }),
  });
}

/**
 * The peer distribution for a position group in one competition+season: every
 * player in `positions`, one row each (grouped by player_id). No player filter —
 * this IS the population the Passing widgets rank a player against and average
 * for the "positional avg" / radar overlay. Disabled until positions are known
 * (an empty group means "no peer comparison"). The minutes floor is applied
 * client-side (see passing.ts) so changing it never refetches.
 */
export function usePlayerStatsPeers(
  competitionId: number | null,
  seasonId: number | null,
  keys: string[],
  positions: string[]
): UseQueryResult<StatsResponse, Error> {
  return useQuery({
    queryKey: ["player-stats-peers", competitionId, seasonId, keys, positions],
    enabled:
      competitionId !== null && seasonId !== null && keys.length > 0 && positions.length > 0,
    queryFn: () =>
      apiSend<StatsResponse>("POST", "/player-stats", {
        keys,
        where: {
          competition_id: [competitionId],
          season_id: [seasonId],
          position_id: positions,
        },
        groupBy: ["player_id"],
      }),
  });
}

/**
 * The LEAGUE SEASON AVERAGE for a position group: one server-aggregated row
 * (groupBy []) over every shift in the group — i.e. total events ÷ total minutes,
 * VOLUME-WEIGHTED (high-minute players dominate). This is old Catalyst's default
 * comparison basis for the passing overlay + "positional avg" sub-labels, and it
 * differs from the unweighted per-player mean (`peerMeanRow`). No minutes floor —
 * the pooled aggregate spans the whole group (the floor only trims the per-player
 * distribution used for percentiles/median). Disabled until positions are known.
 */
export function usePlayerStatsGroupAvg(
  competitionId: number | null,
  seasonId: number | null,
  keys: string[],
  positions: string[]
): UseQueryResult<StatsResponse, Error> {
  return useQuery({
    queryKey: ["player-stats-group-avg", competitionId, seasonId, keys, positions],
    enabled:
      competitionId !== null && seasonId !== null && keys.length > 0 && positions.length > 0,
    queryFn: () =>
      apiSend<StatsResponse>("POST", "/player-stats", {
        keys,
        where: {
          competition_id: [competitionId],
          season_id: [seasonId],
          position_id: positions,
        },
        groupBy: [],
      }),
  });
}
